import { useEffect, useState } from 'preact/hooks';
import { authorColor } from './chorus';
import { apiFetch } from '../api/client';
import type { LiveReactionPing } from '../hooks/useThreadEvents';

/**
 * CAPTURE_FIRST_STORY_FLOW — guest-facing floating reactions (doc 70 §6.2).
 *
 * A tap-to-send rail plus Instagram-Live-style glyphs that drift up and fade,
 * each haloed in the reactor's author colour (never an anonymised heart —
 * polyphony). Posts to the v2 guestOrAuth `/react` route; consumes the echoed
 * `live:reaction` ping (so the sender + everyone else see it). Respects
 * `prefers-reduced-motion` by fading in place.
 */
const RAIL = ['❤️', '😂', '😮', '🔥', '👏'];

interface Float {
  id: number;
  emoji: string;
  seed: string;
  left: number;
}

interface Props {
  sessionId: string;
  ping: LiveReactionPing | null;
}

export function LiveReactionLayer({ sessionId, ping }: Props) {
  const [floats, setFloats] = useState<Float[]>([]);

  useEffect(() => {
    if (!ping) return;
    const f: Float = { id: ping.key, emoji: ping.emoji, seed: ping.seed, left: 8 + Math.floor(((ping.key * 37) % 60)) };
    // Bound the on-screen sprite count so a burst can't balloon it.
    setFloats((prev) => [...prev.slice(-18), f]);
    const t = window.setTimeout(() => {
      setFloats((prev) => prev.filter((p) => p.id !== f.id));
    }, 2300);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ping?.key]);

  const send = async (emoji: string) => {
    try {
      await apiFetch(`/live-sessions/${sessionId}/react`, {
        method: 'POST',
        body: JSON.stringify({ emoji }),
      });
    } catch {
      // Fire-and-forget; a dropped reaction is low-stakes.
    }
  };

  return (
    <>
      <div
        aria-hidden="true"
        style={{ position: 'fixed', inset: 0, pointerEvents: 'none', overflow: 'hidden', zIndex: 40 }}
      >
        {floats.map((f) => (
          <FloatGlyph key={f.id} float={f} />
        ))}
      </div>

      <div role="group" aria-label="Send a reaction" style={{ display: 'flex', gap: 'var(--st-space-2)', justifyContent: 'center' }}>
        {RAIL.map((emoji) => (
          <button
            key={emoji}
            type="button"
            onClick={() => send(emoji)}
            aria-label={`React with ${emoji}`}
            style={{
              width: 44,
              height: 44,
              borderRadius: 'var(--st-radius-full)',
              border: '1px solid var(--st-border-soft, rgba(0,0,0,0.12))',
              background: 'var(--st-surface-raised)',
              fontSize: 20,
              cursor: 'pointer',
            }}
          >
            {emoji}
          </button>
        ))}
      </div>
    </>
  );
}

function FloatGlyph({ float }: { float: Float }) {
  const reduceMotion = typeof window !== 'undefined'
    && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  const animate = (el: HTMLSpanElement | null) => {
    if (!el || !el.animate) return;
    if (reduceMotion) {
      el.animate([{ opacity: 0 }, { opacity: 1, offset: 0.2 }, { opacity: 0 }], { duration: 2200, easing: 'ease-out' });
    } else {
      el.animate(
        [
          { transform: 'translateY(0) scale(0.6)', opacity: 0 },
          { transform: 'translateY(-30px) scale(1.15)', opacity: 1, offset: 0.2 },
          { transform: 'translateY(-220px) scale(1)', opacity: 0 },
        ],
        { duration: 2200, easing: 'ease-out' },
      );
    }
  };

  return (
    <span
      ref={animate}
      style={{
        position: 'absolute',
        bottom: 96,
        left: `${float.left}%`,
        fontSize: 30,
        // Author-tinted halo behind the glyph (reads "whose reaction").
        filter: `drop-shadow(0 0 10px ${authorColor(float.seed)})`,
      }}
    >
      {float.emoji}
    </span>
  );
}
